import axios, { AxiosInstance } from 'axios';
import {
  User,
  Certificate,
  CertificateOption,
  CertificatePage,
  CertificateCreate,
  CertificateExcelReport,
  ChargeExcelReport,
  CertificateExtractionResult,
  CertificateSearch,
  CertificateUpdate,
  Charge,
  ChargeCreator,
  ChargePage,
  ChargeCreate,
  ChargeSearch,
  ChargeUpdate,
  Coil,
  Country,
  InternalOrder,
  IssuedZeugnisRef,
  ZeugnisCreatePreview,
  ZeugnisHistoryEntry,
  InternalOrderCreate,
  InternalOrderDetail,
  InternalOrderStatus,
  Lieferant,
  LieferantCreate,
  LieferantUpdate,
  LoginRequest,
  AuthResponse,
  MaterialGrade,
  MaterialGradeCreate,
  MaterialLexware,
  MaterialLexwareCreate,
  MaterialLexwareUpdate,
  MaterialNorm,
  MaterialNormCreate,
  MaterialNormUpdate,
  ScanResponse,
  SearchResult,
  Surface,
  SurfaceCreate,
  UserCreate,
  UserUpdate,
} from '../types';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || '/api',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        const requestUrl: string = error.config?.url ?? '';
        const isLoginRequest = requestUrl.includes('/auth/login');
        if (error.response?.status === 401 && !isLoginRequest) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>('/auth/login', credentials);
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<User>('/auth/me');
    return response.data;
  }

  async logout(): Promise<void> {
    await this.client.post('/auth/logout');
  }

  // User endpoints
  async getUsers(): Promise<User[]> {
    const response = await this.client.get<User[]>('/users/');
    return response.data;
  }

  async getUser(userId: number): Promise<User> {
    const response = await this.client.get<User>(`/users/${userId}`);
    return response.data;
  }

  async createUser(userData: UserCreate): Promise<User> {
    const response = await this.client.post<User>('/users/', userData);
    return response.data;
  }

  async updateUser(userId: number, userData: UserUpdate): Promise<User> {
    const response = await this.client.put<User>(`/users/${userId}`, userData);
    return response.data;
  }

  async deleteUser(userId: number): Promise<void> {
    await this.client.delete(`/users/${userId}`);
  }

  async resetPassword(userId: number, newPassword: string): Promise<void> {
    await this.client.post(`/users/${userId}/reset-password`, { new_password: newPassword });
  }

  // Certificate endpoints
  async getCertificates(): Promise<Certificate[]> {
    const response = await this.client.get<Certificate[]>('/certificates/');
    return response.data;
  }

  async getCertificatePage(
    params: Record<string, string | number | undefined>,
    signal?: AbortSignal
  ): Promise<CertificatePage> {
    const response = await this.client.get<CertificatePage>('/certificates/page', { params, signal });
    return response.data;
  }

  async getCertificateOptions(signal?: AbortSignal): Promise<{ items: CertificateOption[]; total: number }> {
    const response = await this.client.get<{ items: CertificateOption[]; total: number }>(
      '/certificates/options',
      { signal }
    );
    return response.data;
  }

  async getCertificate(certificateId: number): Promise<Certificate> {
    const response = await this.client.get<Certificate>(`/certificates/${certificateId}`);
    return response.data;
  }

  async searchCertificates(searchParams: CertificateSearch): Promise<SearchResult> {
    const response = await this.client.get<SearchResult>('/certificates/search', {
      params: searchParams,
    });
    return response.data;
  }

  async certificateNumberTaken(
    number: string,
    excludeId?: number
  ): Promise<{ exists: boolean; certificate_id: number | null }> {
    const response = await this.client.get<{ exists: boolean; certificate_id: number | null }>(
      '/certificates/number-taken',
      { params: { number, ...(excludeId != null ? { exclude_id: excludeId } : {}) } }
    );
    return response.data;
  }

  async createCertificate(
    certificateData: CertificateCreate,
    pdfFile?: File
  ): Promise<Certificate> {
    const formData = new FormData();
    formData.append('certificate_number', certificateData.certificate_number);
    formData.append('lieferant_id', certificateData.lieferant_id.toString());
    formData.append('certificate_date', certificateData.certificate_date);
    formData.append('material_grade_id', certificateData.material_grade_id.toString());
    if (certificateData.surface_id != null) {
      formData.append('surface_id', certificateData.surface_id.toString());
    }
    if (certificateData.country_id != null) {
      formData.append('country_id', certificateData.country_id.toString());
    }
    if (certificateData.material_norm_id != null) {
      formData.append('material_norm_id', certificateData.material_norm_id.toString());
    }
    if (certificateData.notes) {
      formData.append('notes', certificateData.notes);
    }
    if (certificateData.metal_values?.length) {
      formData.append('metal_values', JSON.stringify(certificateData.metal_values));
    }
    if (certificateData.mechanical_values?.length) {
      formData.append('mechanical_values', JSON.stringify(certificateData.mechanical_values));
    }
    if (certificateData.melt_country_id != null) {
      formData.append('melt_country_id', certificateData.melt_country_id.toString());
    }
    if (certificateData.heat_number) {
      formData.append('heat_number', certificateData.heat_number);
    }
    if (certificateData.blocked) {
      formData.append('blocked', 'true');
    }
    if (certificateData.melt_works) {
      formData.append('melt_works', certificateData.melt_works);
    }
    formData.append('business_unit', certificateData.business_unit);
    if (pdfFile) {
      formData.append('pdf_file', pdfFile);
    }

    const response = await this.client.post<Certificate>('/certificates/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateCertificate(
    certificateId: number,
    certificateData: CertificateUpdate,
    pdfFile?: File
  ): Promise<Certificate> {
    const formData = new FormData();
    if (certificateData.certificate_number) {
      formData.append('certificate_number', certificateData.certificate_number);
    }
    if (certificateData.lieferant_id) {
      formData.append('lieferant_id', certificateData.lieferant_id.toString());
    }
    if (certificateData.certificate_date) {
      formData.append('certificate_date', certificateData.certificate_date);
    }
    if (certificateData.material_grade_id) {
      formData.append('material_grade_id', certificateData.material_grade_id.toString());
    }
    if (certificateData.surface_id !== undefined) {
      formData.append(
        'surface_id',
        certificateData.surface_id === null ? '' : certificateData.surface_id.toString()
      );
    }
    if (certificateData.country_id !== undefined) {
      formData.append(
        'country_id',
        certificateData.country_id === null ? '' : certificateData.country_id.toString()
      );
    }
    if (certificateData.melt_country_id !== undefined) {
      formData.append(
        'melt_country_id',
        certificateData.melt_country_id == null ? '' : certificateData.melt_country_id.toString()
      );
    }
    if (certificateData.blocked !== undefined) {
      formData.append('blocked', certificateData.blocked ? 'true' : 'false');
    }
    if (certificateData.heat_number !== undefined) {
      formData.append('heat_number', certificateData.heat_number);
    }
    if (certificateData.melt_works !== undefined) {
      formData.append('melt_works', certificateData.melt_works);
    }
    if (certificateData.material_norm_id !== undefined) {
      formData.append(
        'material_norm_id',
        certificateData.material_norm_id === null ? '' : certificateData.material_norm_id.toString()
      );
    }
    if (certificateData.notes !== undefined) {
      formData.append('notes', certificateData.notes ?? '');
    }
    if (certificateData.metal_values !== undefined) {
      formData.append('metal_values', JSON.stringify(certificateData.metal_values || []));
    }
    if (certificateData.mechanical_values !== undefined) {
      formData.append('mechanical_values', JSON.stringify(certificateData.mechanical_values || []));
    }
    if (certificateData.business_unit !== undefined) {
      formData.append('business_unit', certificateData.business_unit);
    }
    if (pdfFile) {
      formData.append('pdf_file', pdfFile);
    }

    const response = await this.client.put<Certificate>(
      `/certificates/${certificateId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async deleteCertificate(certificateId: number): Promise<void> {
    await this.client.delete(`/certificates/${certificateId}`);
  }

  async downloadCertificatePdf(certificateId: number): Promise<Blob> {
    const response = await this.client.get(`/certificates/${certificateId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async exportCertificatesExcel(): Promise<Blob> {
    const response = await this.client.get('/certificates/export/excel', {
      responseType: 'blob',
    });
    return response.data;
  }

  async downloadCertificatesTemplate(): Promise<Blob> {
    const response = await this.client.get('/certificates/template/excel', {
      responseType: 'blob',
    });
    return response.data;
  }

  async importCertificatesExcel(file: File, commit: boolean): Promise<CertificateExcelReport> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post<CertificateExcelReport>(
      `/certificates/import/excel?commit=${commit ? 'true' : 'false'}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async extractFromPdf(file: File): Promise<CertificateExtractionResult> {
    const formData = new FormData();
    formData.append('pdf_file', file);
    const response = await this.client.post<CertificateExtractionResult>(
      '/certificates/extract-pdf',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  // Material Grade endpoints
  async getMaterialGrades(): Promise<MaterialGrade[]> {
    const response = await this.client.get<MaterialGrade[]>('/master-data/material-grades');
    return response.data;
  }

  async getMaterialGrade(gradeId: number): Promise<MaterialGrade> {
    const response = await this.client.get<MaterialGrade>(
      `/master-data/material-grades/${gradeId}`
    );
    return response.data;
  }

  async createMaterialGrade(gradeData: MaterialGradeCreate): Promise<MaterialGrade> {
    const response = await this.client.post<MaterialGrade>(
      '/master-data/material-grades',
      gradeData
    );
    return response.data;
  }

  async updateMaterialGrade(
    gradeId: number,
    gradeData: MaterialGradeCreate
  ): Promise<MaterialGrade> {
    const response = await this.client.put<MaterialGrade>(
      `/master-data/material-grades/${gradeId}`,
      gradeData
    );
    return response.data;
  }

  async deleteMaterialGrade(gradeId: number): Promise<void> {
    await this.client.delete(`/master-data/material-grades/${gradeId}`);
  }

  // Surface endpoints
  async getSurfaces(): Promise<Surface[]> {
    const response = await this.client.get<Surface[]>('/master-data/surfaces');
    return response.data;
  }

  async getSurface(surfaceId: number): Promise<Surface> {
    const response = await this.client.get<Surface>(`/master-data/surfaces/${surfaceId}`);
    return response.data;
  }

  async createSurface(surfaceData: SurfaceCreate): Promise<Surface> {
    const response = await this.client.post<Surface>('/master-data/surfaces', surfaceData);
    return response.data;
  }

  async updateSurface(surfaceId: number, surfaceData: Partial<Surface>): Promise<Surface> {
    const response = await this.client.put<Surface>(
      `/master-data/surfaces/${surfaceId}`,
      surfaceData
    );
    return response.data;
  }

  async deleteSurface(surfaceId: number): Promise<void> {
    await this.client.delete(`/master-data/surfaces/${surfaceId}`);
  }

  // Country endpoints
  async getCountries(): Promise<Country[]> {
    const response = await this.client.get<Country[]>('/master-data/countries');
    return response.data;
  }

  // Material Lexware endpoints
  async getMaterialLexwares(): Promise<MaterialLexware[]> {
    const response = await this.client.get<MaterialLexware[]>('/master-data/material-lexware');
    return response.data;
  }

  async getMaterialLexware(lexwareId: number): Promise<MaterialLexware> {
    const response = await this.client.get<MaterialLexware>(
      `/master-data/material-lexware/${lexwareId}`
    );
    return response.data;
  }

  async createMaterialLexware(lexwareData: MaterialLexwareCreate): Promise<MaterialLexware> {
    const response = await this.client.post<MaterialLexware>(
      '/master-data/material-lexware',
      lexwareData
    );
    return response.data;
  }

  async updateMaterialLexware(
    lexwareId: number,
    lexwareData: MaterialLexwareUpdate
  ): Promise<MaterialLexware> {
    const response = await this.client.put<MaterialLexware>(
      `/master-data/material-lexware/${lexwareId}`,
      lexwareData
    );
    return response.data;
  }

  async deleteMaterialLexware(lexwareId: number): Promise<void> {
    await this.client.delete(`/master-data/material-lexware/${lexwareId}`);
  }

  // Material Lexware Excel endpoints
  async exportMaterialLexwareExcel(): Promise<Blob> {
    const response = await this.client.get('/master-data/material-lexware/export/excel', {
      responseType: 'blob',
    });
    return response.data;
  }

  async downloadMaterialLexwareTemplate(): Promise<Blob> {
    const response = await this.client.get('/master-data/material-lexware/template/excel', {
      responseType: 'blob',
    });
    return response.data;
  }

  async importMaterialLexwareExcel(file: File): Promise<{
    message: string;
    created: number;
    updated: number;
    total_processed: number;
    errors?: string[];
    additional_errors?: number;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post(
      '/master-data/material-lexware/import/excel',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  // Material Norm endpoints
  async getMaterialNorms(): Promise<MaterialNorm[]> {
    const response = await this.client.get<MaterialNorm[]>('/master-data/material-norms');
    return response.data;
  }

  async createMaterialNorm(data: MaterialNormCreate): Promise<MaterialNorm> {
    const response = await this.client.post<MaterialNorm>('/master-data/material-norms', data);
    return response.data;
  }

  async updateMaterialNorm(id: number, data: MaterialNormUpdate): Promise<MaterialNorm> {
    const response = await this.client.put<MaterialNorm>(`/master-data/material-norms/${id}`, data);
    return response.data;
  }

  async deleteMaterialNorm(id: number): Promise<void> {
    await this.client.delete(`/master-data/material-norms/${id}`);
  }

  // Lieferant endpoints
  async getLieferanten(): Promise<Lieferant[]> {
    const response = await this.client.get<Lieferant[]>('/master-data/lieferanten');
    return response.data;
  }

  async getLieferant(lieferantId: number): Promise<Lieferant> {
    const response = await this.client.get<Lieferant>(`/master-data/lieferanten/${lieferantId}`);
    return response.data;
  }

  async createLieferant(lieferantData: LieferantCreate): Promise<Lieferant> {
    const response = await this.client.post<Lieferant>('/master-data/lieferanten', lieferantData);
    return response.data;
  }

  async updateLieferant(lieferantId: number, lieferantData: LieferantUpdate): Promise<Lieferant> {
    const response = await this.client.put<Lieferant>(
      `/master-data/lieferanten/${lieferantId}`,
      lieferantData
    );
    return response.data;
  }

  async deleteLieferant(lieferantId: number): Promise<void> {
    await this.client.delete(`/master-data/lieferanten/${lieferantId}`);
  }

  // Charge endpoints
  async getCharges(): Promise<Charge[]> {
    const response = await this.client.get<Charge[]>('/charges/');
    return response.data;
  }

  async getChargePage(
    params: Record<string, string | number | undefined>,
    signal?: AbortSignal
  ): Promise<ChargePage> {
    const response = await this.client.get<ChargePage>('/charges/page', { params, signal });
    return response.data;
  }

  async exportChargesExcel(): Promise<Blob> {
    const response = await this.client.get('/charges/export/excel', { responseType: 'blob' });
    return response.data;
  }

  async downloadChargesTemplate(): Promise<Blob> {
    const response = await this.client.get('/charges/template/excel', { responseType: 'blob' });
    return response.data;
  }

  async importChargesExcel(file: File, commit: boolean): Promise<ChargeExcelReport> {
    const form = new FormData();
    form.append('file', file);
    const response = await this.client.post<ChargeExcelReport>(
      `/charges/import/excel?commit=${commit ? 'true' : 'false'}`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async getChargeCreators(signal?: AbortSignal): Promise<ChargeCreator[]> {
    const response = await this.client.get<ChargeCreator[]>('/charges/creators', { signal });
    return response.data;
  }

  async getCharge(chargeId: number): Promise<Charge> {
    const response = await this.client.get<Charge>(`/charges/${chargeId}`);
    return response.data;
  }

  async searchCharges(searchParams: ChargeSearch): Promise<Charge[]> {
    const response = await this.client.get<Charge[]>('/charges/search', {
      params: searchParams,
    });
    return response.data;
  }

  async createCharge(chargeData: ChargeCreate): Promise<Charge> {
    const response = await this.client.post<Charge>('/charges/', chargeData);
    return response.data;
  }

  async updateCharge(chargeId: number, chargeData: ChargeUpdate): Promise<Charge> {
    const response = await this.client.put<Charge>(`/charges/${chargeId}`, chargeData);
    return response.data;
  }

  async deleteCharge(chargeId: number): Promise<void> {
    await this.client.delete(`/charges/${chargeId}`);
  }

  async getChargeCoils(chargeId: number): Promise<Coil[]> {
    const response = await this.client.get<Coil[]>(`/charges/${chargeId}/coils`);
    return response.data;
  }

  // Internal Order endpoints
  async listInternalOrders(opts?: {
    status?: InternalOrderStatus;
    mine?: boolean;
    q?: string;
  }): Promise<InternalOrder[]> {
    const params = new URLSearchParams();
    if (opts?.status) params.set('status', opts.status);
    if (opts?.mine) params.set('mine', 'true');
    if (opts?.q && opts.q.trim()) params.set('q', opts.q.trim());
    const qs = params.toString();
    const url = qs ? `/internal-orders/?${qs}` : '/internal-orders/';
    const response = await this.client.get<InternalOrder[]>(url);
    return response.data;
  }

  async getInternalOrder(orderId: number): Promise<InternalOrderDetail> {
    const response = await this.client.get<InternalOrderDetail>(`/internal-orders/${orderId}`);
    return response.data;
  }

  async createInternalOrder(payload: InternalOrderCreate): Promise<InternalOrder> {
    const response = await this.client.post<InternalOrder>('/internal-orders/', payload);
    return response.data;
  }

  async scanCoil(orderId: number, barcode: string): Promise<ScanResponse> {
    const response = await this.client.post<ScanResponse>(`/internal-orders/${orderId}/scan`, {
      barcode,
    });
    return response.data;
  }

  async unlinkCoil(orderId: number, coilId: number): Promise<void> {
    await this.client.delete(`/internal-orders/${orderId}/coils/${coilId}`);
  }

  async closeInternalOrder(orderId: number): Promise<InternalOrder> {
    const response = await this.client.post<InternalOrder>(`/internal-orders/${orderId}/close`);
    return response.data;
  }

  async deleteInternalOrder(orderId: number): Promise<void> {
    await this.client.delete(`/internal-orders/${orderId}`);
  }

  async downloadInternalOrderCert(
    orderId: number,
    format: 'pdf' | 'zip' = 'pdf',
    vorlage: 'kunde' | 'zoll_eu' = 'kunde'
  ): Promise<{ blob: Blob; filename: string }> {
    const params = new URLSearchParams();
    params.set('format', format);
    params.set('vorlage', vorlage);
    const url = `/internal-orders/${orderId}/kalotte-cert?${params.toString()}`;
    const response = await this.client.get<Blob>(url, { responseType: 'blob' });

    const disposition = (response.headers as any)['content-disposition'] as string | undefined;
    const match = disposition?.match(/filename="([^"]+)"/);
    const ext = format === 'pdf' ? 'pdf' : 'zip';
    const filename = match?.[1] ?? `Kalotte-Zeugnis-${orderId}.${ext}`;

    return { blob: response.data, filename };
  }

  async previewChargeNumber(chargeData: ChargeCreate): Promise<{
    charge_number: string;
    lieferant_short_name: string;
    lieferant_name: string;
    material_nummer: string;
    sequence_number: number;
  }> {
    const response = await this.client.post('/charges/preview-charge-number', chargeData);
    return response.data;
  }

  async listIssuedZeugnisse(certificateId: number): Promise<IssuedZeugnisRef[]> {
    const response = await this.client.get(`/certificates/${certificateId}/issued-zeugnisse`);
    return response.data;
  }

  async listZeugnisHistory(chargeId: number): Promise<ZeugnisHistoryEntry[]> {
    const response = await this.client.get(`/charges/${chargeId}/zeugnis-history`);
    return response.data;
  }

  async zeugnisCreatePreview(chargeId: number): Promise<ZeugnisCreatePreview> {
    const response = await this.client.get(`/charges/${chargeId}/zeugnis-preview`);
    return response.data;
  }

  async downloadKalotteCert(
    chargeId: number,
    format: 'xlsx' | 'pdf' = 'xlsx',
    lieferschein?: string,
    vorlage: 'kunde' | 'zoll_eu' = 'kunde',
    temporary = false,
    issueId?: number
  ): Promise<{ blob: Blob; filename: string }> {
    const params = new URLSearchParams();
    params.set('format', format);
    if (issueId != null) {
      params.set('issue_id', String(issueId));
    } else {
      params.set('vorlage', vorlage);
      if (temporary) {
        params.set('temporary', 'true');
      }
      if (lieferschein && lieferschein.trim().length > 0) {
        params.set('lieferschein', lieferschein);
      }
    }
    const url = `/charges/${chargeId}/kalotte-cert?${params.toString()}`;
    const response = await this.client.get<Blob>(url, { responseType: 'blob' });

    const disposition = (response.headers as any)['content-disposition'] as string | undefined;
    const match = disposition?.match(/filename="([^"]+)"/);
    const filename = match?.[1] ?? `Kalotte-Zeugnis-${chargeId}.${format}`;

    return { blob: response.data, filename };
  }
}

export const apiService = new ApiService();
